import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartLineUp, Warning, TrendUp, TrendDown, Package, CalendarBlank, CurrencyCircleDollar, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fc = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function BIDashboard() {
  const [data, setData] = useState(null);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/bi/dashboard`).then(r => setData(r.data))
      .catch(() => toast.error("Erro ao carregar BI"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;
  if (!data) return null;

  const { kpis, alerts, monthly_trend } = data;
  const revChange = kpis.revenue_prev > 0 ? ((kpis.revenue - kpis.revenue_prev) / kpis.revenue_prev * 100) : 0;

  const getCmvColor = (pct) => pct <= 30 ? "text-emerald-700" : pct <= 40 ? "text-amber-700" : "text-rose-700";

  return (
    <div className="space-y-6" data-testid="bi-dashboard-page">
      <div>
        <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("bi_title")}</h2>
        <p className="text-sm text-zinc-500 mt-1">{t("bi_subtitle")} — {data.month}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Receita</p>
            <p className="text-lg md:text-2xl font-mono font-bold text-zinc-900 mt-1" data-testid="bi-revenue">{fc(kpis.revenue)}</p>
            {revChange !== 0 && (
              <p className={`text-xs font-semibold mt-0.5 flex items-center gap-1 ${revChange > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                {revChange > 0 ? <TrendUp size={12} /> : <TrendDown size={12} />}
                {revChange > 0 ? "+" : ""}{revChange.toFixed(1)}% vs mês anterior
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-rose-600">CMV</p>
            <p className={`text-lg md:text-2xl font-mono font-bold mt-1 ${getCmvColor(kpis.cmv_pct)}`} data-testid="bi-cmv">{kpis.cmv_pct}%</p>
            <p className="text-xs text-zinc-500 mt-0.5">Meta: 28-35%</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Desperdício</p>
            <p className="text-lg md:text-2xl font-mono font-bold text-amber-700 mt-1">{kpis.waste_pct}%</p>
            <p className="text-xs text-zinc-500 mt-0.5">{fc(kpis.waste_cost)} no mês</p>
          </CardContent>
        </Card>
        <Card className={`border-2 shadow-sm ${kpis.net_profit >= 0 ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}>
          <CardContent className="pt-4 pb-3 px-4">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${kpis.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>Lucro Líquido</p>
            <p className={`text-lg md:text-2xl font-mono font-bold mt-1 ${kpis.net_profit >= 0 ? "text-emerald-800" : "text-rose-800"}`} data-testid="bi-profit">{fc(kpis.net_profit)}</p>
            <p className={`text-xs font-semibold mt-0.5 ${kpis.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>Margem: {kpis.net_margin}%</p>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {(alerts.low_stock > 0 || alerts.expiring_lots > 0 || alerts.price_alerts.length > 0) && (
        <Card className="border-amber-200 bg-amber-50 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold flex items-center gap-2 text-amber-800">
              <Warning size={18} weight="fill" /> Alertas Inteligentes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {alerts.low_stock > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-900">
                <Package size={16} /> <span><strong>{alerts.low_stock}</strong> ingrediente(s) com estoque baixo</span>
              </div>
            )}
            {alerts.expiring_lots > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-900">
                <CalendarBlank size={16} /> <span><strong>{alerts.expiring_lots}</strong> lote(s) vencendo em 7 dias</span>
              </div>
            )}
            {alerts.price_alerts.map((pa, i) => (
              <div key={`pa-${i}`} className="flex items-center gap-2 text-sm text-amber-900">
                <CurrencyCircleDollar size={16} />
                <span><strong>{pa.ingredient}</strong>: preço {pa.change_pct > 0 ? "subiu" : "caiu"} {Math.abs(pa.change_pct)}% ({fc(pa.old_price)} → {fc(pa.new_price)})</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Revenue Trend */}
      <Card className="border-zinc-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <ChartLineUp size={18} /> Tendência Mensal (6 meses)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {monthly_trend.map(m => (
              <div key={m.month} className="flex items-center gap-3">
                <span className="text-xs text-zinc-500 w-16 font-mono">{m.month}</span>
                <div className="flex-1 bg-zinc-100 rounded-full h-6 relative overflow-hidden">
                  {m.revenue > 0 && (
                    <>
                      <div className="absolute left-0 top-0 h-full bg-rose-300 rounded-l-full"
                        style={{ width: `${Math.min(m.cmv_pct, 100)}%` }} />
                      <div className="absolute left-0 top-0 h-full bg-emerald-400 rounded-full"
                        style={{ width: `${Math.min(100 - m.cmv_pct, 100)}%`, marginLeft: `${m.cmv_pct}%` }} />
                    </>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-zinc-800">
                    {m.revenue > 0 ? `${fc(m.revenue)} | CMV ${m.cmv_pct}%` : "Sem dados"}
                  </div>
                </div>
                <span className={`text-xs font-mono font-bold w-20 text-right ${m.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fc(m.profit)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-[10px] text-zinc-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-rose-300 rounded-full" /> CMV</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-400 rounded-full" /> Margem</span>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4 text-center">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Vendas no Mês</p>
            <p className="text-3xl font-mono font-bold text-zinc-900 mt-1">{kpis.sales_count}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4 text-center">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">OPs Concluídas</p>
            <p className="text-3xl font-mono font-bold text-zinc-900 mt-1">{kpis.orders_completed}</p>
          </CardContent>
        </Card>
        <Card className="border-zinc-200 shadow-sm">
          <CardContent className="pt-4 pb-3 px-4 text-center">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Ticket Médio</p>
            <p className="text-3xl font-mono font-bold text-zinc-900 mt-1">{kpis.sales_count > 0 ? fc(kpis.revenue / kpis.sales_count) : fc(0)}</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
